import React, { useState } from 'react';
import {
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { ResourceMatchResult, MatchSignal } from '@/types/recommendation';
import { Button } from '@/components/ui/button';
import { ResourceIdentityBlock } from './ResourceIdentityBlock';
import { MatchScoreBadge } from './MatchScoreBadge';
import { cn } from '@/lib/utils';
import { useRagThresholds, getRagAccent } from '@/hooks/useRagThresholds';
import { Progress } from '@/components/ui/progress';

// ── Availability pill — matches the bulk "Review Suggested Resources" style ──
function AvailabilityPill({ status }: { status: 'free' | 'partial' | 'conflict' }) {
  if (status === 'free')
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800 whitespace-nowrap">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        Free
      </span>
    );
  if (status === 'partial')
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800 whitespace-nowrap">
        <Clock className="h-3 w-3 shrink-0" />
        Partial
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800 whitespace-nowrap">
      <AlertTriangle className="h-3 w-3 shrink-0" />
      Conflict
    </span>
  );
}

interface ResourceRecommendationCardProps {
  result: ResourceMatchResult;
  isAlreadyAdded: boolean;
  /**
   * When true the card renders in the "already selected" state on first render —
   * used to pre-select the best-match candidate so the planner can confirm
   * immediately without having to click Add first.
   */
  isPreSelected?: boolean;
  onAdd: (result: ResourceMatchResult) => void;
  requestedStart: Date;
  requestedEnd: Date;
  /** Required skills from the booking — passed through to identity block for highlighting */
  requiredSkills?: string[];
}

/** Expanded breakdown row — label, bar, explanation */
function SignalDetailRow({ signal }: { signal: MatchSignal }) {
  const thresholds = useRagThresholds();
  const accent = getRagAccent(signal.score, thresholds);

  const scoreColour =
    accent === 'success'
      ? 'text-emerald-600'
      : accent === 'warning'
      ? 'text-amber-500'
      : 'text-destructive';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">{signal.label}</span>
        <span className={cn('text-xs font-bold tabular-nums', scoreColour)}>
          {signal.score}%
        </span>
      </div>
      <Progress
        variant="mini"
        value={signal.score}
        accent={accent}
        aria-label={`${signal.label}: ${signal.score}%`}
      />
      {signal.explanation && (
        <p className="text-xs text-muted-foreground leading-snug">{signal.explanation}</p>
      )}
    </div>
  );
}

export function ResourceRecommendationCard({
  result,
  isAlreadyAdded,
  isPreSelected = false,
  onAdd,
  requestedStart,
  requestedEnd,
  requiredSkills = [],
}: ResourceRecommendationCardProps) {
  // Score detail is expanded by default — the single-booking view always shows
  // the full breakdown so planners don't have to click to reveal it.
  const [expanded, setExpanded] = useState(true);

  // Treat pre-selected the same as already-added for the button/border state.
  const isSelected = isAlreadyAdded || isPreSelected;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !isSelected) {
      e.preventDefault();
      onAdd(result);
    }
  };

  return (
    <div
      role="article"
      aria-label={`Resource: ${result.resource.name}, match score: ${result.overallScore}%`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn(
        'group rounded-xl border bg-card transition-all duration-200',
        'hover:shadow-md hover:-translate-y-0.5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        isSelected && 'border-primary/40 bg-primary/[0.02]'
      )}
    >
      {/* ── Top section: identity + score + action ── */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          {/* Identity: avatar, name, role, location, skills with pips */}
          <div className="flex-1 min-w-0">
            <ResourceIdentityBlock
              resource={result.resource}
              size="lg"
              requiredSkills={requiredSkills}
            />
          </div>

          {/* Score + Add button — stacked right */}
          <div className="flex flex-col items-end gap-2 shrink-0 pt-0.5">
            <MatchScoreBadge score={result.overallScore} />
            {isSelected ? (
              <div className="flex items-center gap-1.5 text-xs text-primary font-medium px-2.5 py-1.5 rounded-lg bg-primary/10">
                <CheckCircle className="h-3.5 w-3.5" />
                Added
              </div>
            ) : (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="h-8 px-4 text-xs"
                onClick={() => onAdd(result)}
                aria-label={`Add ${result.resource.name}`}
              >
                Add
              </Button>
            )}
          </div>
        </div>

        {/* Availability pill — matches the bulk view pill style (✓ Free / ⚠ Partial) */}
        <div className="mt-3 flex items-center gap-2">
          <AvailabilityPill status={result.availabilityStatus} />
        </div>
      </div>

      {/* ── Expandable full breakdown — open by default ── */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground',
          'border-t border-dashed cursor-pointer rounded-b-xl',
          'hover:text-foreground hover:bg-muted/30 transition-all duration-150',
          expanded && 'text-foreground bg-muted/20'
        )}
        aria-label={expanded ? 'Hide score detail' : 'Show score detail'}
        aria-expanded={expanded}
      >
        <span className="font-semibold uppercase tracking-widest text-[10px]">
          Score detail
        </span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 transition-transform duration-150" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-150" />
        )}
      </button>

      {/* Score breakdown — 2-column grid, colour-coded bars + plain English explanation */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t grid grid-cols-2 gap-x-6 gap-y-4">
          {result.signals.map(s => (
            <SignalDetailRow key={s.key} signal={s} />
          ))}
        </div>
      )}
    </div>
  );
}
